import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isEmailVerified: boolean;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (userData: User, authToken?: string) => void;
    logout: () => void;
    isAuthenticated: boolean;
    isAdmin: boolean;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Carregar dados do localStorage ao iniciar (compatibilidade com sessões antigas)
    useEffect(() => {
        const storedUser = localStorage.getItem('auth_user');
        const storedToken = localStorage.getItem('auth_token');

        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                // Token pode vir do localStorage (legado) ou ser nulo (cookie-based)
                if (storedToken) setToken(storedToken);
            } catch (error) {
                console.error('Erro ao carregar dados de autenticação:', error);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_user');
            }
        }
        setLoading(false);
    }, []);

    const login = (userData: User, authToken?: string) => {
        setUser(userData);
        // Salva dados do usuário para persistir sessão entre recarregamentos
        localStorage.setItem('auth_user', JSON.stringify(userData));
        // Token opcional: quando presente (legado) salva; quando ausente (cookie-based) ignora
        if (authToken) {
            setToken(authToken);
            localStorage.setItem('auth_token', authToken);
        } else {
            // Modo cookie-based: token ficará nulo, autenticação via cookie httpOnly
            setToken('cookie');
            localStorage.setItem('auth_token', 'cookie');
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
    };

    const value = {
        user,
        token,
        login,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        loading
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}